import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose } from "../../Components/UI/dialog";
import { Input } from "../../Components/UI/input";
import { Button } from "../../Components/UI/button";
import { Label } from "../../Components/UI/label";
import { TooltipProvider } from "../../Components/UI/tooltip";
import { Check, X, ArrowLeft, Phone, Contact } from "lucide-react";
import { toast } from "sonner";
import parsePhoneNumberFromString from "libphonenumber-js";
import { manageOtherContactsAPI } from "./ConnectionAPI";
import { AlertBox } from "../../Components/Common/AlertBox";
import { ContactList } from "../../Components/Common/ContactList";
import MobileInput from "../../Components/Common/MobileInput";
import { countries } from "../../Components/Enums/DataEnums";
import { getMobileHeaderTitle, isContactPickerSupported, isMobileDevice, isAndroidDevice, isIOSDevice, isMobileBrowser } from "../../Utils/Utilities";
import useIsMobile from "../../Hooks/useIsMobile"

// Reusable Tailwind classes
const otherContactClasses = {
    spacing: "space-y-1.5",
    input: "h-11 text-sm transition-all duration-200 bg-white sahi-input",
    buttonPrimary: "sahi-primary-button",
    buttonSecondary: "sahi-secondary-button",
    gridCols1: "grid-cols-1",
    gridCols2: "grid-cols-1 sm:grid-cols-2",
    gridCols3: "grid-cols-1 sm:grid-cols-3",
    flexCol: "flex flex-col",
    flexRow: "flex flex-row",
    gap4: "gap-4",
    mb4: "mb-4",
    iconSize: "h-4 w-4",
    iconSizeMd: "h-5 w-5",
    iconSizeLg: "h-6 w-6"
};

const OtherContactsEntry = ({
    connID,
    existingContacts = [],
    editingIndex = null,
    isOpen = false,
    onClose,
    onContactsUpdate,
    bizName = "Contact"
}) => {
    const isMobile = useIsMobile();
    // Remove local contacts state - work directly with existingContacts
    const [editIndex, setEditIndex] = useState(null);
    const [deleteState, setDeleteState] = useState({ open: false, index: null });
    const [isLoading, setIsLoading] = useState(false);
    const [contactList, setContactList] = useState([]);
    const [isContactListOpen, setIsContactListOpen] = useState(false);
    const [contactPermissionDenied, setContactPermissionDenied] = useState(false);

    // Add state for country code
    const [selectedCountryCode, setSelectedCountryCode] = useState("91"); // Default to India

    // Form input values with validation states
    const [formData, setFormData] = useState({
        name: "",
        mobile: "",
        email: "",
        designation: ""
    });

    // Handle editing mode - auto-populate form when editing
    useEffect(() => {
        if (isOpen && editingIndex !== null && existingContacts[editingIndex]) {
            const contactToEdit = existingContacts[editingIndex];
            setEditIndex(editingIndex);
            setFormData({
                name: contactToEdit.name || contactToEdit.CNm || '',
                mobile: contactToEdit.mobile || contactToEdit.MNo || '',
                email: contactToEdit.email || contactToEdit.Eml || '',
                designation: contactToEdit.designation || contactToEdit.Dsgn || ''
            });
        } else if (isOpen && editingIndex === null) {
            // This is add mode, reset everything
            resetForm();
        }
    }, [isOpen, editingIndex, existingContacts]);

    // Open contact picker and fetch contacts from device
    const openContactPicker = async () => {
        if (!isContactPickerSupported()) {
            toast.error("Contact picker not supported on this device");
            return null;
        }

        try {
            window.contacts = null;
            setContactPermissionDenied(false);

            window.location.href = "requestcontactpermission://";
            setTimeout(() => {
                window.location.href = "readcontacts://";
            }, 300);

            const maxAttempts = 16; // ~8s at 500ms
            let attempts = 0;

            const check = setInterval(() => {
                attempts += 1;

                if (typeof window.contacts !== "undefined" && window.contacts !== null && window.contacts !== "") {
                    clearInterval(check);
                    let parsedContacts = [];
                    try {
                        parsedContacts = typeof window.contacts === 'string'
                            ? JSON.parse(window.contacts)
                            : window.contacts;
                    } catch (e) {
                        console.error("JSON Parse failed for contacts:", e);
                    }

                    if (parsedContacts && parsedContacts.length > 0) {
                        setContactList(parsedContacts);
                        setContactPermissionDenied(false);
                    } else {
                        setContactList([]);
                        setContactPermissionDenied(isIOSDevice());
                    }
                    setIsContactListOpen(true);
                    window.contacts = null;
                } else if (attempts >= maxAttempts) {
                    clearInterval(check);
                    setContactList([]);
                    setContactPermissionDenied(isIOSDevice());
                    setIsContactListOpen(true);
                }
            }, 500);
            return null;
        } catch (error) {
            console.log('Contact picker error:', error);
            return null;
        }
    };

    // Re-trigger the native permission prompt (used when contacts came back empty on iOS)
    const retryContactPermission = () => {
        openContactPicker();
    };

    // Parse phone number and extract country code
    const parsePhoneNumber = (phoneNumber) => {
        if (!phoneNumber) return { countryCode: undefined, number: '' }

        const cleanInput = phoneNumber.replace(/\D/g, '')
        const parsed = parsePhoneNumberFromString('+' + cleanInput)

        if (parsed && parsed.isValid()) {
            const isoCode = parsed.country
            const nationalNumber = parsed.nationalNumber

            const countryMatch = countries.find(
                (c) => c.CountryCode.toUpperCase() === isoCode
            )

            return {
                countryCode: countryMatch?.CC,
                country: countryMatch?.Country,
                number: nationalNumber
            }
        }

        const fallbackNumber = cleanInput.slice(-10)
        const defaultCountry =
            countries.find((c) => c.Country === "India") ||
            countries.find((c) => c.CC === '91')

        return {
            countryCode: defaultCountry?.CC,
            number: fallbackNumber
        }
    }

    // Populate form with selected contact data
    const populateFormWithContact = (contactData) => {
        if (!contactData) return;

        const updates = {};

        if (contactData.name) {
            updates.name = contactData.name;
        }

        // Logic to extract the best mobile number
        let selectedPhone = contactData.phone;

        // If the native bridge provides multiple numbers, prioritize "mobile"
        if (contactData.phoneNumbers && Array.isArray(contactData.phoneNumbers)) {
            const mobileEntry = contactData.phoneNumbers.find(p =>
                p.label?.toLowerCase().includes('mobile') ||
                p.type?.toLowerCase().includes('mobile') ||
                p.label?.toLowerCase().includes('cell')
            );
            if (mobileEntry) {
                selectedPhone = mobileEntry.number || mobileEntry.value;
            }
        }

        if (selectedPhone) {
            const { countryCode, country, number } = parsePhoneNumber(selectedPhone);
            updates.mobile = number;

            if (countryCode) {
                setSelectedCountryCode(countryCode);
            }
        }

        if (contactData.email && contactData.email !== '-') {
            updates.email = contactData.email;
        } else {
            updates.email = ''
        }

        if (contactData.org && contactData.org !== '-') {
            updates.designation = contactData.org;
        } else {
            updates.designation = ''
        }

        if (Object.keys(updates).length > 0) {
            setFormData(prev => ({ ...prev, ...updates }));
            toast.success("Contact information selected.");
        }
    };

    // Handle contact picker button click
    const handleContactPickerClick = async () => {
        if (!isMobileDevice()) {
            toast.info("Contact picker is only available on mobile devices");
            return;
        }

        if (!isContactPickerSupported()) {
            toast.error("Contact picker not supported on this device");
            return;
        }

        try {
            const contactData = await openContactPicker();
            if (contactData) {
                populateFormWithContact(contactData);
            }
        } catch (error) {
            console.error('Error in handleContactPickerClick:', error);
            // Fallback to contact list dialog if native picker fails
            setIsContactListOpen(true);
        }
    };

    // Form validation with comprehensive rules
    const validateForm = () => {
        const validationRules = [
            {
                field: 'name',
                condition: !formData.name.trim(),
                message: 'Please enter a name.',
                description: 'Name is required.'
            },
            {
                field: 'name',
                condition: formData.name.trim().length < 2,
                message: 'Please enter a valid name.',
                description: 'Name must be at least 2 characters long.'
            },
            {
                field: 'mobile',
                // condition: !formData.mobile.trim(),
                condition: false,
                message: 'Please enter a valid mobile number.',
                description: 'Mobile number is required.'
            }
        ];

        // Dynamic mobile validation based on selected country
        const selectedCountry = countries.find(c => c.CC == selectedCountryCode);
        const countryCode = selectedCountry?.CC || "91";

        if (countryCode === "91") {
            // India - 10 digits
            validationRules.push({
                field: 'mobile',
                // condition: !/^\d{10}$/.test(formData.mobile.trim()),
                condition: false,
                message: 'Please enter a valid mobile number.',
                description: 'Enter a valid 10-digit mobile number for India.'
            });
        } else {
            // Other countries - 7-15 digits
            validationRules.push({
                field: 'mobile',
                // condition: !/^\d{7,15}$/.test(formData.mobile.trim()),
                condition: false,
                message: 'Please enter a valid mobile number.',
                description: 'Enter a valid mobile number (7-15 digits).'
            });
        }

        // Email format validation using existingContacts instead of local state
        if (formData.email && formData.email.trim() && formData.email.trim() !== '') {
            const emailRegex = /^[^\s@.]+@[^\s@.]+(\.([^\s@.])+)+$/;
            if (!emailRegex.test(formData.email.trim())) {
                return {
                    isValid: false,
                    message: 'Please enter a valid email address.',
                    description: 'Invalid Email Format'
                };
            }

            // Then check for duplicate emails
            const isDuplicateEmail = existingContacts.some((contact, index) => {
                const contactEmail = contact.email || contact.Eml || '';
                return contactEmail && contactEmail.trim() &&
                    contactEmail.toLowerCase() === formData.email.trim().toLowerCase() &&
                    index !== editIndex;
            });

            if (isDuplicateEmail) {
                return {
                    isValid: false,
                    message: 'Duplicate Email Address',
                    description: 'This email address already exists.'
                };
            }
        }

        // Check validation rules
        for (const rule of validationRules) {
            if (rule.condition) {
                return {
                    isValid: false,
                    field: rule.field,
                    message: rule.message,
                    description: rule.description
                };
            }
        }

        // Check for duplicate mobile numbers using existingContacts
        const isDuplicateMobile = existingContacts.some((contact, index) => {
            const contactMobile = contact.mobile || contact.MNo || '';
            return contactMobile === formData.mobile.trim() && index !== editIndex;
        });

        if (isDuplicateMobile) {
            return {
                isValid: false,
                message: 'Duplicate Mobile Number',
                description: 'This mobile number already exists.'
            };
        }

        return { isValid: true };
    };

    // Submit handler for adding/editing contacts
    const handleSaveContact = async () => {
        const validation = validateForm();
        if (!validation.isValid) {
            toast.error(validation.message, {
                description: validation.description,
                duration: 4000
            });
            return;
        }

        setIsLoading(true);
        try {
            const contactData = {
                name: formData.name.trim(),
                mobile: formData.mobile.trim(),
                email: formData.email.trim().toLowerCase(),
                designation: formData.designation.trim(),
                countryCode: selectedCountryCode
            };
            let updatedContacts;
            if (editIndex !== null) {
                // Editing existing contact - preserve CID if it exists
                const existingContact = existingContacts[editIndex];
                if (existingContact.CID) {
                    contactData.CID = existingContact.CID;
                }
                updatedContacts = [...existingContacts];
                updatedContacts[editIndex] = contactData;
            } else {
                // Adding new contact
                updatedContacts = [...existingContacts, contactData];
            }

            // Make API call to save contacts with proper error handling
            await manageOtherContactsAPI(connID, updatedContacts);

            // Show success message
            toast.success(editIndex !== null ? "Contact updated!" : "Contact added.", {
            });

            // Notify parent component to refresh data
            if (onContactsUpdate) {
                onContactsUpdate(updatedContacts);
            }

            resetForm();
            // Close the dialog after saving
            if (onClose) {
                onClose();
            }
        } catch (error) {
            console.error("Error saving contact:", error);
            const errorMessage = error.isBusinessLogicError
                ? error.message
                : "Failed to save contact. Please try again or contact support if the issue persists.";
            toast.error(errorMessage, {
            });
        } finally {
            setIsLoading(false);
        }
    };

    // Handle input field changes
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    // Reset form data
    const resetForm = () => {
        setFormData({
            name: "",
            mobile: "",
            email: "",
            designation: ""
        });
        setEditIndex(null);
        setSelectedCountryCode("91"); // Reset to India
    };

    // Close dialog and reset states
    const handleClose = () => {
        resetForm();
        if (onClose) {
            onClose();
        }
    };

    // Handle delete contact
    const handleDeleteContact = async () => {
        if (deleteState.index === null) return;

        setIsLoading(true);
        try {
            // Filter out the contact at the specified index
            const updatedContacts = existingContacts.filter((_, index) => index !== deleteState.index);

            // Make API call to save updated contacts
            await manageOtherContactsAPI(connID, updatedContacts);

            toast.success("Contact deleted.",);

            // Notify parent component to refresh data
            if (onContactsUpdate) {
                onContactsUpdate(updatedContacts);
            }

            setDeleteState({ open: false, index: null });
        } catch (error) {
            console.error("Error deleting contact:", error);
            const errorMessage = error.isBusinessLogicError
                ? error.message
                : "Failed to delete contact. Please try again or contact support if the issue persists.";
            toast.error(errorMessage, {
            });
        } finally {
            setIsLoading(false);
        }
    };

    // Contact form fields configuration
    const contactFields = [
        {
            id: "name",
            name: "name",
            label: "Name",
            placeholder: "e.g. John Doe",
            type: "text",
            required: true,
            maxLength: 50
        },
        {
            id: "mobile",
            name: "mobile",
            label: "Mobile",
            placeholder: "e.g. 9876543210",
            type: "mobile",
            required: false,
            maxLength: 15
        },
        {
            id: "email",
            name: "email",
            label: "Email",
            placeholder: "e.g. john@gmail.com",
            type: "email",
            required: false,
            maxLength: 320
        },
        // {
        //     id: "designation",
        //     name: "designation",
        //     label: "Designation",
        //     placeholder: "e.g. Manager",
        //     type: "text",
        //     required: false,
        //     maxLength: 100
        // }
    ];

    // Render form fields with consistent styling matching ThreadEntry
    const renderFormField = (field) => {
        const isRequiredField = field.required;

        // Handle mobile field separately
        if (field.type === 'mobile') {
            return (
                <MobileInput
                    key={field.id}
                    fieldID={field.id}
                    label={field.label}
                    placeholder={field.placeholder}
                    value={formData[field.name]}
                    onChange={handleInputChange}
                    selectedCountryCode={selectedCountryCode}
                    onCountryCodeChange={setSelectedCountryCode}
                    required={isRequiredField}
                    className={otherContactClasses.spacing}
                />
            );
        }

        // Handle other fields
        return (
            <div key={field.id} className={otherContactClasses.spacing}>
                <Label htmlFor={field.id} required={isRequiredField}>
                    {field.label}
                </Label>
                <div className="relative">
                    <Input
                        id={field.id}
                        name={field.name}
                        type={field.type}
                        value={formData[field.name]}
                        onChange={handleInputChange}
                        placeholder={field.placeholder}
                        maxLength={field.maxLength}
                        className={`${otherContactClasses.input} ${field.id === 'name' && (isAndroidDevice() || isIOSDevice()) && !isMobileBrowser() ? 'pr-12' : ''}`}
                        disabled={isLoading}
                    />
                    {field.id === 'name' && (isAndroidDevice() || isIOSDevice()) && !isMobileBrowser() && (
                        <button
                            type="button"
                            onClick={isLoading ? undefined : handleContactPickerClick}
                            disabled={isLoading}
                            className={`absolute right-1 top-1/2 transform -translate-y-1/2 h-9 w-9 flex items-center justify-center rounded-md shrink-0 focus:outline-none transition-colors ${isLoading ? 'cursor-not-allowed opacity-50' : 'hover:bg-gray-50'}`}
                            title={isLoading ? undefined : "Select from contacts"}
                            tabIndex={-1}
                        >
                            <Contact className="w-5 h-5 text-gray-600" />
                        </button>
                    )}
                </div>
            </div>
        );
    };

    // Render action buttons with consistent styling (Save Contact first, then Close)
    const renderActionButtons = () => (
        <div className="py-4 sm:px-0 sm:sticky sm:bottom-0 sm:bg-background sm:pb-3 border-t sm:border-t-0">
            <div className={`${otherContactClasses.flexCol} ${otherContactClasses.gap4} sm:flex-row sm:justify-start sm:gap-3 w-full`}>
                {/* Mobile Layout - Save Contact button full width */}
                <div className="flex sm:hidden w-full">
                    <Button
                        onClick={handleSaveContact}
                        className={`${otherContactClasses.buttonPrimary} w-full`}
                        disabled={isLoading}
                    >
                        {isLoading ? (
                            <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                Saving...
                            </>
                        ) : (
                            <>
                                <Check className={otherContactClasses.iconSize} />
                                {editIndex !== null ? 'Update Contact' : 'Save Contact'}
                            </>
                        )}
                    </Button>
                </div>

                {/* Desktop Layout - Save Contact first, then Close */}
                <Button
                    onClick={handleSaveContact}
                    className={`${otherContactClasses.buttonPrimary} hidden sm:flex sm:min-w-[140px]`}
                    disabled={isLoading}
                >
                    {isLoading ? (
                        <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            Saving...
                        </>
                    ) : (
                        <>
                            <Check className={otherContactClasses.iconSize} />
                            {editIndex !== null ? 'Update Contact' : 'Save Contact'}
                        </>
                    )}
                </Button>

                {/* Desktop Layout - Close button with icon */}
                <Button
                    variant="secondary"
                    onClick={handleClose}
                    disabled={isLoading}
                    className="hidden sm:flex"
                >
                    <X className={otherContactClasses.iconSizeMd} />
                    <span>Close</span>
                </Button>
            </div>
        </div>
    );

    const renderHeader = () => (
        <DialogHeader className="gap-0 px-3 sm:px-4 md:px-4">


            <div className="sm:hidden flex items-center justify-between w-full h-8">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                    <DialogClose tabIndex={-1} className="p-0 w-auto h-auto flex-shrink-0 flex justify-center">
                        <ArrowLeft className={otherContactClasses.iconSizeLg} />
                        <span className="sr-only">Back</span>
                    </DialogClose>
                    <DialogTitle className="text-lg font-medium">
                        <div className="truncate">
                            {editIndex !== null
                                ? getMobileHeaderTitle('Edit Other Contact Person')
                                : getMobileHeaderTitle('Other Contact Person Entry')
                            }
                        </div>
                    </DialogTitle>
                </div>

            </div>
            <DialogTitle className="hidden sm:block text-lg md:text-xl">
                {editIndex !== null
                    ? `Edit Other Contact Person - ${bizName}`
                    : `Other Contact Person Entry - ${bizName}`}

            </DialogTitle>




            <div className="hidden sm:flex items-center gap-2">
                <DialogClose
                    className="flex-shrink-0"
                    tabIndex={-1}
                    onClick={handleClose}
                    disabled={isLoading}
                >
                    <X className={otherContactClasses.iconSizeMd} />
                    <span className="sr-only">Close</span>
                </DialogClose>
            </div>
        </DialogHeader>
    );

    // Only render the form dialog when opened from ConnectionDetails
    return (
        <TooltipProvider>
            <Dialog
                open={isOpen}
                onOpenChange={(open) => {
                    if (!open && !isLoading) {
                        handleClose();
                    }
                }}
                preventOutsideClick={true}
            >
                <DialogContent className="flex flex-col w-full h-full max-h-screen sm:w-[90vw] sm:max-w-[600px] sm:min-h-0 sm:max-h-[95vh] sm:h-auto sm:rounded-lg rounded-none sahi-radius !gap-0 bg-gray-100">
                    {renderHeader()}

                    <div className="px-4 pt-4 flex-1 scrollbar-no-arrows overflow-y-scroll sm:max-h-[85vh] sm:pb-0 bg-background">
                        <div className="space-y-4">
                            {/* Disabled Connection Field for Mobile */}
                            {(() => {
                                const isShow = isMobile && connID && bizName;

                                return isShow ? (
                                    <div key='connID' className="space-y-1.5">
                                        <Label htmlFor='connID' required={true}>
                                            Contact
                                        </Label>
                                        <Input
                                            id='connID'
                                            name='connID'
                                            value={bizName}
                                            onChange={() => { }} // No-op since it's disabled
                                            placeholder={'Contact'}
                                            className={otherContactClasses.input}
                                            type={'text'}
                                            autoComplete={'off'}
                                            maxLength={50}
                                            disabled={true}
                                        />
                                    </div>
                                ) : null;
                            })()}

                            {/* Row 1: Name and Mobile */}
                            <div className={`grid ${otherContactClasses.gridCols2} ${otherContactClasses.gap4} ${otherContactClasses.mb4}`}>
                                {contactFields.slice(0, 2).map(field => renderFormField(field))}
                            </div>

                            {/* Row 2: Email and Designation */}
                            <div className={`grid ${otherContactClasses.gridCols2} ${otherContactClasses.gap4} ${otherContactClasses.mb4}`}>
                                {contactFields.slice(2).map(field => renderFormField(field))}
                            </div>
                        </div>

                        {renderActionButtons()}
                    </div>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation using AlertBox */}
            <AlertBox
                open={deleteState.open}
                onOpenChange={(open) => setDeleteState({ open, index: null })}
                title="Delete Contact"
                description="Are you sure you want to delete this contact? This action cannot be undone."
                actions={[
                    {
                        label: "Cancel",
                        variant: "cancel",
                        value: "cancel",
                        className: "w-full sm:w-auto"
                    },
                    {
                        label: "Delete",
                        variant: "action",
                        value: "delete",
                        className: "w-full sm:w-auto bg-red-600 hover:bg-red-700 text-white"
                    }
                ]}
                onAction={(actionValue) => {
                    if (actionValue === "delete") {
                        handleDeleteContact();
                    }
                }}
            />

            {/* Contact List Dialog */}
            <ContactList
                externalOpen={isContactListOpen}
                onOpenChange={setIsContactListOpen}
                contacts={contactList}
                permissionDenied={contactPermissionDenied}
                onRequestPermission={retryContactPermission}
                onContactClick={(contact) => {
                    populateFormWithContact(contact);
                    setIsContactListOpen(false);
                }}
            />
        </TooltipProvider>
    );
};

export default OtherContactsEntry;